// Festive module service — API_INTEGRATION.md §3.23
// Manages festive events, orders, alerts, capacity, production planning, and menus.
// RESTAURANT vertical only. Feature flag: ENABLE_FESTIVE_MODULE.

import { apiClient, apiClientPaginated } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type FestiveOrderStatus =
  | 'BOOKED'
  | 'CONFIRMED'
  | 'IN_PRODUCTION'
  | 'READY'
  | 'DELIVERED'
  | 'CANCELLED';

export type FestiveEventStatus = 'UPCOMING' | 'ACTIVE' | 'COMPLETED';

export type AlertUrgency = 'URGENT' | 'UPCOMING' | 'INFORMATIONAL';
export type AlertCategory = 'PREPARATION' | 'INVENTORY' | 'PRE_ORDER' | 'REVENUE';

export type CapacityStatus = 'OPEN' | 'FILLING' | 'FULL' | 'AUTO_STOPPED';

export interface FestivalItem {
  product_id: string;
  product_name: string;
  price: number;        // paise
  max_quantity: number | null;
}

export interface Festival {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  start_date: string;
  end_date: string;
  booking_deadline: string;
  items: FestivalItem[];
  total_capacity: number | null;
  booked_count: number;
  advance_percentage: number;
  location_ids: string[];
  is_active: boolean;
  created_at: string;
  // Derived client-side from dates and is_active
  status: FestiveEventStatus;
  // Aliases for UI compatibility
  order_count: number;       // = booked_count
  revenue_forecast: number;  // paise; not available from backend, defaults to 0
}

// Tenant-wide festive-event KPI aggregate (GitHub issue #293). Computed
// server-side across every festival so Total Forecast / Upcoming / Active stay
// constant as you paginate, instead of being derived from the current page.
export interface FestivalStats {
  total_count: number;
  upcoming_count: number;
  active_count: number;
  total_forecast: number; // paise
}

export interface FestiveOrder {
  id: string;
  tenant_id: string;
  festival_id: string;
  festival_name: string;
  customer_name: string;
  customer_phone: string;
  status: FestiveOrderStatus;
  order_type: 'PICKUP' | 'DELIVERY' | 'DINE_IN';
  items: Array<{
    product_id: string;
    product_name: string;
    quantity: number;
    unit_price: number; // paise
    notes?: string;
  }>;
  total: number;              // paise
  deposit_amount: number;     // paise
  deposit_paid: boolean;
  delivery_date: string;
  delivery_time_slot: string | null;
  delivery_address: string | null;
  special_instructions: string | null;
  cancellation_charge: number; // paise
  location_id: string;
  location_name: string;
  created_at: string;
  updated_at: string;
}

export interface FestiveAlert {
  id: string;
  festival_id: string;
  festival_name: string;
  alert_type: string;
  priority: string;
  message: string;
  days_until: number;
  acknowledged: boolean;
  acknowledged_by?: string | null;
  acknowledged_at?: string | null;
  created_at: string;
}

export interface CapacityDay {
  date: string;
  max_orders: number;
  booked_orders: number;
  remaining: number;
  status: CapacityStatus;
  kitchen_load_pct: number;
}

export interface CapacitySettings {
  max_orders_per_day: number;
  auto_stop_enabled: boolean;
}

export interface ProductionPlanItem {
  product_name: string;
  total_ordered: number;
  ingredients_needed: Array<{
    name: string;
    quantity: number;
    unit: string;
    in_stock: number;
    shortfall: number;
  }>;
}

export interface ProductionPlan {
  festival_name: string;
  date: string;
  items: ProductionPlanItem[];
  prep_schedule: Array<{ time_slot: string; items: string[] }>;
  total_items: number;
  total_ingredients: number;
  procurement_cost_estimate: number; // paise
}

export interface FestiveMenuItem {
  id: string;
  festival_id: string;
  product_id: string;
  product_name: string;
  image_url: string | null;
  category: string;
  regular_price: number;  // paise
  festive_price: number;  // paise
  bulk_pricing_tiers: Array<{
    min_quantity: number;
    max_quantity: number;
    discount_percentage: number;
  }>;
  linked_recipe_id: string | null;
  is_available: boolean;
  created_at: string;
}

// ─── Request DTOs ───────────────────────────────────────────────────────────

export interface CreateFestivalDto {
  name: string;
  description?: string;
  start_date: string;
  end_date: string;
  booking_deadline?: string;     // defaults to end_date T23:59:59Z on backend
  items?: Array<{
    product_id: string;
    price_override?: number;     // paise
    max_quantity?: number;
  }>;
  total_capacity?: number | null;
  advance_percentage?: number;
  location_ids?: string[];
}

export interface CreateFestiveOrderDto {
  festival_id: string;
  customer_name: string;
  customer_phone: string;
  order_type: 'PICKUP' | 'DELIVERY' | 'DINE_IN';
  pickup_date: string;
  pickup_time: string;
  delivery_address?: string;
  items: Array<{
    product_id: string;
    quantity: number;
    notes?: string;
  }>;
  advance_amount: number; // paise
  payment_method: 'CASH' | 'UPI' | 'CARD';
  special_instructions?: string;
  location_id: string;
}

export interface ModifyFestiveOrderDto {
  items?: Array<{
    product_id: string;
    quantity: number;
    notes?: string;
  }>;
  pickup_date?: string;
  pickup_time?: string;
  delivery_address?: string;
  special_instructions?: string;
}

export interface CancelFestiveOrderDto {
  reason: string;
  reason_notes?: string;
  refund_method: 'CASH' | 'ORIGINAL_PAYMENT' | 'STORE_CREDIT';
}

export interface AddFestiveMenuItemDto {
  product_id: string;
  category: string;
  festive_price: number; // paise
  bulk_pricing_tiers?: Array<{
    min_quantity: number;
    max_quantity: number;
    discount_percentage: number;
  }>;
  linked_recipe_id?: string;
  is_available?: boolean;
}

// ─── Query params ───────────────────────────────────────────────────────────

export interface FestiveOrderListParams {
  page?: number;
  limit?: number;
  festival_id?: string;
  status?: FestiveOrderStatus;
  delivery_date?: string;
  search?: string;
  location_id?: string;
}

// Tenant-/location-wide KPI aggregate (GitHub issue #288). Computed server-side
// across every festive order matching the current filters so the "Confirmed"
// KPI stays constant as you paginate, instead of being counted from the current
// ≤20-row page slice.
export interface FestiveOrderStats {
  confirmed_count: number;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

function deriveFestivalStatus(f: { start_date: string; end_date: string; is_active: boolean }): FestiveEventStatus {
  if (!f.is_active) return 'COMPLETED';
  const today = new Date().toISOString().slice(0, 10);
  if (f.start_date > today) return 'UPCOMING';
  if (f.end_date >= today) return 'ACTIVE';
  return 'COMPLETED';
}

function normaliseFestival(raw: Omit<Festival, 'status' | 'order_count' | 'revenue_forecast'> & Partial<Festival>): Festival {
  return {
    ...raw,
    items: raw.items ?? [],
    location_ids: raw.location_ids ?? [],
    booking_deadline: raw.booking_deadline ?? '',
    status: deriveFestivalStatus(raw),
    order_count: raw.booked_count ?? 0,
    // Server now returns per-festival forecast (sum of non-cancelled booking
    // revenue, paise). Fall back to 0 for older payloads that omit it.
    revenue_forecast: raw.revenue_forecast != null ? Number(raw.revenue_forecast) : 0,
  };
}

// ─── Service ────────────────────────────────────────────────────────────────

export const festiveService = {
  // ── Festivals (Events) ──────────────────────────────────────────────────

  async listFestivals(params?: {
    page?: number;
    status?: string;
    upcoming?: boolean;
    is_active?: boolean;
    limit?: number;
  }): Promise<{
    data: Festival[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const q = buildQuery({
      page: params?.page,
      status: params?.status,
      upcoming: params?.upcoming,
      is_active: params?.is_active,
      limit: params?.limit,
    });
    const res = await apiClientPaginated<Festival[]>(`/festivals${q}`);
    return { ...res, data: res.data.map(normaliseFestival) };
  },

  // Tenant-wide festive-event KPIs (GitHub issue #293) — real totals across all
  // festivals, so Total Forecast / Upcoming / Active stay constant across pages.
  async getFestivalStats(): Promise<FestivalStats> {
    return apiClient<FestivalStats>('/festivals/stats');
  },

  async getFestival(id: string): Promise<Festival> {
    const raw = await apiClient<Festival>(`/festivals/${id}`);
    return normaliseFestival(raw);
  },

  async createFestival(dto: CreateFestivalDto): Promise<Festival> {
    const raw = await apiClient<Festival>('/festivals', { method: 'POST', body: dto });
    return normaliseFestival(raw);
  },

  async updateFestival(id: string, dto: Partial<CreateFestivalDto>): Promise<Festival> {
    const raw = await apiClient<Festival>(`/festivals/${id}`, { method: 'PATCH', body: dto });
    return normaliseFestival(raw);
  },

  async deleteFestival(id: string): Promise<void> {
    return apiClient<void>(`/festivals/${id}`, { method: 'DELETE' });
  },

  // ── Festive Orders ──────────────────────────────────────────────────────

  async listFestiveOrders(params: FestiveOrderListParams = {}): Promise<{
    data: FestiveOrder[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const q = buildQuery({
      page: params.page,
      limit: params.limit,
      festival_id: params.festival_id,
      status: params.status,
      delivery_date: params.delivery_date,
      search: params.search,
      location_id: params.location_id,
    });
    return apiClientPaginated<FestiveOrder[]>(`/festive-orders${q}`);
  },

  async getFestiveOrderStats(params: FestiveOrderListParams = {}): Promise<FestiveOrderStats> {
    const q = buildQuery({
      festival_id: params.festival_id,
      status: params.status,
      delivery_date: params.delivery_date,
      search: params.search,
      location_id: params.location_id,
    });
    return apiClient<FestiveOrderStats>(`/festive-orders/stats${q}`);
  },

  async getFestiveOrder(id: string): Promise<FestiveOrder> {
    return apiClient<FestiveOrder>(`/festive-orders/${id}`);
  },

  async createFestiveOrder(dto: CreateFestiveOrderDto): Promise<FestiveOrder> {
    return apiClient<FestiveOrder>('/festive-orders', { method: 'POST', body: dto });
  },

  async updateFestiveOrderStatus(id: string, status: FestiveOrderStatus): Promise<FestiveOrder> {
    return apiClient<FestiveOrder>(`/festive-orders/${id}/status`, { method: 'PATCH', body: { status } });
  },

  async modifyFestiveOrder(id: string, dto: ModifyFestiveOrderDto): Promise<FestiveOrder> {
    return apiClient<FestiveOrder>(`/festive-orders/${id}`, { method: 'PATCH', body: dto });
  },

  async cancelFestiveOrder(id: string, dto: CancelFestiveOrderDto): Promise<FestiveOrder> {
    return apiClient<FestiveOrder>(`/festive-orders/${id}/cancel`, { method: 'POST', body: dto });
  },

  // ── Alerts ──────────────────────────────────────────────────────────────

  async getAlerts(params?: { tab?: string; status?: string }): Promise<FestiveAlert[]> {
    const q = buildQuery({ category: params?.tab, status: params?.status });
    return apiClient<FestiveAlert[]>(`/festivals/alerts${q}`);
  },

  async dismissAlert(id: string): Promise<void> {
    return apiClient<void>(`/festivals/alerts/${id}/dismiss`, { method: 'PUT' });
  },

  async getAlertHistory(): Promise<FestiveAlert[]> {
    return apiClient<FestiveAlert[]>('/festivals/alerts/history');
  },

  // ── Capacity ────────────────────────────────────────────────────────────

  // The backend returns an aggregate object ({ total_capacity, booked_count,
  // bookings_by_date, items }). The capacity page works in per-day rows, so
  // project bookings_by_date into CapacityDay[] using total_capacity as the
  // daily ceiling (Save Settings writes that same field).
  async getCapacity(festivalId: string): Promise<CapacityDay[]> {
    const res = await apiClient<{
      total_capacity: number | null;
      bookings_by_date?: Array<{ delivery_date: string; count: number }>;
    }>(`/festivals/${festivalId}/capacity`);
    const maxOrders = Number(res.total_capacity ?? 0);
    return (res.bookings_by_date ?? []).map((d) => {
      const booked = Number(d.count);
      const fillPct = maxOrders > 0 ? (booked / maxOrders) * 100 : 0;
      const status: CapacityStatus =
        fillPct >= 100 ? 'FULL' : fillPct >= 80 ? 'FILLING' : 'OPEN';
      return {
        date: String(d.delivery_date).slice(0, 10),
        max_orders: maxOrders,
        booked_orders: booked,
        remaining: Math.max(0, maxOrders - booked),
        status,
        kitchen_load_pct: Math.round(fillPct),
      };
    });
  },

  async updateCapacity(festivalId: string, settings: CapacitySettings): Promise<void> {
    return apiClient<void>(`/festivals/${festivalId}/capacity`, { method: 'PUT', body: settings });
  },

  async overrideAutoStop(festivalId: string): Promise<void> {
    return apiClient<void>(`/festivals/${festivalId}/capacity/override`, { method: 'PUT' });
  },

  // ── Production Planning ─────────────────────────────────────────────────

  async getProductionPlanning(festivalId: string, params?: { date?: string }): Promise<ProductionPlan> {
    const q = buildQuery({ date: params?.date });
    return apiClient<ProductionPlan>(`/festivals/${festivalId}/production-plan${q}`);
  },

  // ── Festive Menu ────────────────────────────────────────────────────────

  async getFestiveMenu(festivalId: string, params?: { category?: string }): Promise<FestiveMenuItem[]> {
    const q = buildQuery({ category: params?.category });
    return apiClient<FestiveMenuItem[]>(`/festivals/${festivalId}/menu${q}`);
  },

  async addFestiveMenuItem(festivalId: string, dto: AddFestiveMenuItemDto): Promise<FestiveMenuItem> {
    return apiClient<FestiveMenuItem>(`/festivals/${festivalId}/menu`, { method: 'POST', body: dto });
  },

  async updateFestiveMenuItem(
    festivalId: string,
    itemId: string,
    dto: Partial<AddFestiveMenuItemDto>,
  ): Promise<FestiveMenuItem> {
    return apiClient<FestiveMenuItem>(`/festivals/${festivalId}/menu/${itemId}`, {
      method: 'PUT',
      body: dto,
    });
  },

  async deleteFestiveMenuItem(festivalId: string, itemId: string): Promise<void> {
    return apiClient<void>(`/festivals/${festivalId}/menu/${itemId}`, { method: 'DELETE' });
  },
};
